import { useRef, useState } from 'react';

import { withRedemptionToken } from '@utils/redemptionRoute';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams } from 'react-router';

import { RedemptionLayout } from '../../redemption/components/RedemptionLayout';
import { LoginForm } from '../components/LoginForm';
import { SignupForm } from '../components/SignupForm';
import { SocialAuthButtons } from '../components/SocialAuthButtons';
import { LoginValues, SignupValues, useAuth } from '../context/AuthContext';

type AuthPageProps = {
  mode: 'login' | 'signup';
};

export const AuthPage = ({ mode }: AuthPageProps) => {
  const { login, signup, signInWithGoogle, signInWithFacebook, signInWithApple, user, loading } =
    useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isPending, setIsPending] = useState(false);
  const pending = useRef(false);
  const token = searchParams.get('token');
  const isLoginMode = mode === 'login';

  const authenticate = async (action: () => Promise<void>) => {
    if (pending.current) return;

    pending.current = true;
    setIsPending(true);
    try {
      await action();
    } catch {
      // AuthProvider displays the provider-specific error and recovery guidance.
    } finally {
      pending.current = false;
      setIsPending(false);
    }
  };

  if (loading) {
    return (
      <RedemptionLayout step={0} showCodeNote={!!token}>
        <p className="redemption-body" role="status">
          {t('auth_page.loading')}
        </p>
      </RedemptionLayout>
    );
  }

  if (user && token) {
    return <Navigate to={withRedemptionToken('/redeem', token)} replace />;
  }

  const handleLogin = ({ email, password }: LoginValues) =>
    authenticate(() => login(email, password));
  const handleSignup = ({ email, password }: SignupValues) =>
    authenticate(() => signup(email, password));

  return (
    <RedemptionLayout step={0} showCodeNote={!!token}>
      <div className="redemption-purpose">
        <KeyRound size={16} aria-hidden="true" />
        {t('flow.eyebrow')}
      </div>
      <Link to={withRedemptionToken('/redeem', token)} className="redemption-back">
        <ArrowLeft size={15} aria-hidden="true" />
        {t('flow.back_to_choice')}
      </Link>
      <h1>{t(isLoginMode ? 'auth_page.login_title' : 'auth_page.signup_title')}</h1>
      <p className="redemption-body">
        {t(isLoginMode ? 'auth_page.login_help' : 'auth_page.signup_help')}
      </p>
      <SocialAuthButtons
        onGoogleSignIn={() => authenticate(signInWithGoogle)}
        onFacebookSignIn={() => authenticate(signInWithFacebook)}
        onAppleSignIn={() => authenticate(signInWithApple)}
        showAppleSignIn={isLoginMode}
        disabled={isPending}
      />
      <div className="redemption-divider">
        <span>{t('auth_page.or_divider')}</span>
      </div>
      {isLoginMode ? (
        <LoginForm onSubmit={handleLogin} disabled={isPending} />
      ) : (
        <SignupForm onSubmit={handleSignup} disabled={isPending} />
      )}
      <p className="redemption-auth-switch">
        {t(isLoginMode ? 'auth_page.no_account' : 'auth_page.already_have_account')}{' '}
        <Link
          to={withRedemptionToken(isLoginMode ? '/signup' : '/login', token)}
          className="redemption-text-link"
        >
          {t(isLoginMode ? 'auth_page.sign_up_link' : 'auth_page.log_in_link')}
        </Link>
      </p>
    </RedemptionLayout>
  );
};
